import { useState, useEffect } from "react";
import { useQuery } from "@apollo/client";
import { GET_ALLOCATION_TYPES } from "../graphql/allocation";

export interface AllocationType {
  key: string;
  name: string;
  integrationSourceSystem: string;
  integrationEntity: string;
  visible: boolean;
  editable: boolean;
  isLinkedCustomField: string | null;
  /** Only when asked for with `withCounts`; above ALLOCATION_COUNT_CAP means more than the cap. */
  allocationCount?: number;
}

export const ALLOCATION_COUNT_CAP = 100;

export interface PageInfo {
  hasNextPage: boolean;
  endCursor: string;
}

export const useAllocationTypes = ({
  showAll = false,
  withCounts = false,
}: { showAll?: boolean; withCounts?: boolean } = {}) => {
  const [allocationTypes, setAllocationTypes] = useState<AllocationType[]>([]);

  const {
    loading: loadingAllocationTypes,
    data: allocationTypesData,
    refetch: refetchAllocationTypes,
  } = useQuery(GET_ALLOCATION_TYPES, {
    variables: { showAll, withCounts },
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (allocationTypesData?.allocationTypes) {
      const types = allocationTypesData.allocationTypes.map((node: any) => ({
        key: node.key,
        name: node.name,
        integrationSourceSystem: node.integrationSourceSystem || "",
        integrationEntity: node.integrationEntity || "",
        visible: node.visible ?? false,
        editable: node.editable ?? false,
        isLinkedCustomField: node.isLinkedCustomField ?? null,
        ...(typeof node.allocationCount === "number" && {
          allocationCount: node.allocationCount,
        }),
      }));
      setAllocationTypes(types);
    }
  }, [allocationTypesData]);

  ///////////////////////////////// REFETCH /////////////////////////////////

  async function handleRefetchAllocationTypes(): Promise<void> {
    try {
      await refetchAllocationTypes();
    } catch (error) {
      console.error("Error refetching allocation types:", error);
    }
  }

  return {
    allocationTypes,
    loading: loadingAllocationTypes,
    refetchAllocationTypes: handleRefetchAllocationTypes,
  };
};

/** A type a custom field collects stays selectable, whatever its stored setting says. */
export const isAllocationTypeHidden = (
  type: Pick<AllocationType, "visible" | "isLinkedCustomField">,
): boolean => !type.visible && !type.isLinkedCustomField;
